import { useEffect, useMemo, useRef, useState } from "react";
import {
  CheckCircle,
  Zap,
  RotateCw,
  Coins,
  AlertTriangle,
  Activity,
  Gauge,
  CalendarDays,
  Trophy,
  Compass,
  Check,
  BarChart3,
  Timer,
} from "lucide-react";
import {
  Chart as ChartJS,
  ArcElement,
  LineElement,
  BarElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import Layout from "../../layouts/Layout";
import StatusDot from "../../components/common/StatusDot";
import "../../assets/styles/colors.css";
import { useAuth } from "../../context/AuthContext";
import {
  getMyOverview,
  normalizeMyOverview,
  getTokenActivity,
  normalizeTokenActivity,
  getSoloProgression,
  normalizeSoloProgression,
  getCategoryCoverage,
  normalizeCategoryCoverage,
  getDifficultyStats,
  normalizeDifficultyStats,
  getActivityDeployments,
  normalizeActivityDeployments,
} from "../../api/services/professionalDashboardService";

ChartJS.register(
  ArcElement,
  LineElement,
  BarElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Filler,
  Tooltip,
  Legend,
);

function useThemeColors() {
  const [c, setC] = useState(null);
  useEffect(() => {
    const readColors = () => {
      const root = getComputedStyle(document.documentElement);
      const v = (name, fallback) =>
        (root.getPropertyValue(name) || "").trim() || fallback;
      setC({
        primary: v("--color-primary", "#00e639"),
        primaryHover: v("--color-primary-hover", "#00c430"),
        accentViolet: v("--color-accent-violet", "#8b5cf6"),
        textPrimary: v("--color-text-primary", "#dfe2eb"),
        textSecondary: v("--color-text-secondary", "#8fa3be"),
        textMuted: v("--color-text-muted", "#3e4f65"),
        border: v("--color-border", "#212a38"),
        surface: v("--color-surface", "#181c22"),
        bg: v("--color-bg", "#0f1219"),
        blue: "#3b82f6",
        violet: "#8b5cf6",
        orange: "#f59e0b",
        red: "#ef4444",
      });
    };

    readColors();
    const observer = new MutationObserver(readColors);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);
  return c;
}

function withAlpha(hex, a) {
  const m = hex.replace("#", "");
  const n =
    m.length === 3
      ? m
          .split("")
          .map((x) => x + x)
          .join("")
      : m;
  const r = parseInt(n.slice(0, 2), 16);
  const g = parseInt(n.slice(2, 4), 16);
  const b = parseInt(n.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${a})`;
}

function useCountUp(target, run, dur = 1300) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!run) return;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / dur, 1);
      const e = 1 - Math.pow(1 - p, 3);
      setVal(target * e);
      if (p < 1) raf = requestAnimationFrame(tick);
      else setVal(target);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, run, dur]);
  return Math.round(val).toLocaleString();
}

function useInView(threshold = 0.1) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setInView(true);
            obs.unobserve(e.target);
          }
        });
      },
      { threshold },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

const fmtK = (v) =>
  v >= 1000 ? (v / 1000).toFixed(1).replace(/\.0$/, "") + "K" : String(v);

/* ── Building blocks ─────────────────────────── */
function SectionLabel({ children }) {
  return (
    <div className="text-[10.5px] tracking-[0.22em] uppercase font-semibold text-(--color-text-muted) font-mono">
      {children}
    </div>
  );
}

function Card({ children, className = "", style }) {
  return (
    <div className={`qv-card qv-noise ${className}`} style={style}>
      {children}
    </div>
  );
}

/* Pretty, actionable error state shown inside any section's Card */
function SectionError({ message, onRetry }) {
  return (
    <div className="flex flex-col items-center justify-center text-center gap-3 px-5 py-9">
      <div className="w-11 h-11 rounded-full flex items-center justify-center bg-red-500/10 border border-red-500/20">
        <AlertTriangle size={20} className="text-red-400" />
      </div>
      <div>
        <div className="text-[13px] font-semibold text-(--color-text-primary)">
          Couldn’t load this section
        </div>
        <div className="text-[12px] text-(--color-text-muted) mt-0.5">
          {message || "Something went wrong fetching the data."}
        </div>
      </div>
      {onRetry && (
        <button
          onClick={onRetry}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold text-(--color-primary) bg-(--color-primary)/8 border border-(--color-primary)/20 hover:bg-(--color-primary)/14 hover:-translate-y-px transition-all"
        >
          <RotateCw size={13} />
          Try again
        </button>
      )}
    </div>
  );
}

function tipPreset(colors) {
  return {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderWidth: 1,
    titleColor: colors.textPrimary,
    bodyColor: colors.textSecondary,
    padding: 10,
    cornerRadius: 8,
    displayColors: true,
    boxWidth: 8,
    boxHeight: 8,
    boxPadding: 4,
  };
}

/* Hover-gated glow for line charts — glows the whole line while the mouse
   is over the canvas. Mirrors the other dashboards. Falls back to glowColor
   when borderColor is a gradient function. */
const lineGlowPlugin = {
  id: "lineGlow",
  afterDatasetsDraw(chart) {
    if (!chart._lineGlowHover) return;
    const { ctx } = chart;
    chart.data.datasets.forEach((dataset, i) => {
      const meta = chart.getDatasetMeta(i);
      if (meta.hidden || meta.type !== "line" || !meta.dataset) return;
      const glow =
        dataset.glowColor ||
        (typeof dataset.borderColor === "string"
          ? dataset.borderColor
          : "#ffffff");
      [8, 18].forEach((blur) => {
        ctx.save();
        ctx.shadowColor = glow;
        ctx.shadowBlur = blur;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;
        meta.dataset.draw(ctx, chart.chartArea);
        ctx.restore();
      });
    });
  },
  afterEvent(chart, args) {
    const was = chart._lineGlowHover;
    const now = args.event.type !== "mouseout";
    if (was !== now) {
      chart._lineGlowHover = now;
      chart.draw();
    }
  },
};

/* Glow plugin for bar charts — glows the hovered bar */
const barGlowPlugin = {
  id: "barGlow",
  afterDatasetsDraw(chart) {
    const active = chart.getActiveElements();
    if (!active.length) return;
    const { ctx } = chart;
    active.forEach(({ element, datasetIndex, index }) => {
      const ds = chart.data.datasets[datasetIndex];
      const color = Array.isArray(ds.backgroundColor)
        ? ds.backgroundColor[index]
        : ds.backgroundColor;
      ctx.save();
      ctx.shadowColor = color;
      ctx.shadowBlur = 22;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;
      element.draw(ctx);
      ctx.restore();
    });
  },
};

/* ─────────────────────────────────────────────
   Section 1: My Overview (4 stat cards — no team tile)
   ───────────────────────────────────────────── */
function MyOverview({ colors }) {
  const [ref, inView] = useInView();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reload, setReload] = useState(0);
  const retry = () => setReload((n) => n + 1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const raw = await getMyOverview();
        if (!cancelled) setData(normalizeMyOverview(raw));
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reload]);

  const run = inView && !!data;
  const completed = useCountUp(data?.boxesCompleted ?? 0, run);
  const created = useCountUp(data?.boxesCreated ?? 0, run);
  const tokens = useCountUp(data?.tokensRemaining ?? 0, run);

  /* ── Error state ───────────────────────────── */
  if (error) {
    return (
      <section ref={ref}>
        <div className="mb-4">
          <SectionLabel>My Overview</SectionLabel>
        </div>
        <Card>
          <SectionError message={error} onRetry={retry} />
        </Card>
      </section>
    );
  }

  /* ── Loading skeletons ─────────────────────── */
  if (loading || !data) {
    return (
      <section ref={ref}>
        <div className="mb-4">
          <SectionLabel>My Overview</SectionLabel>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4.5">
          {[0, 1, 2, 3].map((i) => (
            <Card key={i} className="p-5.5">
              <div className="animate-pulse flex flex-col gap-3">
                <div className="flex items-center gap-2.75">
                  <div className="w-10 h-10 rounded-full bg-white/5" />
                  <div className="h-3 w-28 rounded bg-white/5" />
                </div>
                <div className="h-9 w-24 rounded bg-white/5" />
                <div className="h-8 w-full rounded bg-white/5" />
              </div>
            </Card>
          ))}
        </div>
      </section>
    );
  }

  /* ── Derived values ────────────────────────── */
  const totalCreated = data.boxesCreated || 1;
  const capUsedPct =
    data.dailyCap > 0
      ? Math.round((data.tokensUsedToday / data.dailyCap) * 100)
      : 0;
  const capLeftPct = 100 - capUsedPct;

  const sparkPath =
    "M0,28 L18,26 L36,27 L54,22 L72,23 L90,18 L108,19 L126,14 L144,13 L162,9 L180,7 L200,4";

  const segTotal = 10;
  const segFilled = Math.round((data.firstTrySuccess / 100) * segTotal);

  return (
    <section ref={ref}>
      <div className="mb-4">
        <SectionLabel>My Overview</SectionLabel>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4.5">
        {/* ── Boxes Completed ────────────────── */}
        <Card
          className="p-5 qv-ping"
          style={{ "--ping-x": "50%", "--ping-y": "50%" }}
        >
          <div className="flex items-center gap-2.75 mb-3.5">
            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-(--color-primary)/15">
              <CheckCircle size={20} className="text-(--color-primary)" />
            </div>
            <span className="text-[13px] text-(--color-text-primary)">
              Boxes Completed
            </span>
          </div>
          <div className="qv-num text-[36px] mb-1.5 text-(--color-text-primary)">
            {completed}
          </div>
          <div className="text-[12.5px] text-(--color-text-muted)">
            out of{" "}
            <span className="text-(--color-text-secondary) font-semibold">
              {data.boxesAttempted}
            </span>{" "}
            attempted
          </div>
          <svg
            viewBox="0 0 200 34"
            preserveAspectRatio="none"
            className="w-full h-8.5 mt-3 transition-[filter] duration-300 hover:filter-[drop-shadow(0_0_4px_var(--color-primary))_drop-shadow(0_0_10px_var(--color-primary))]"
          >
            <defs>
              <linearGradient id="pro-spark" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor={withAlpha(colors.primary, 0.28)} />
                <stop offset="1" stopColor={withAlpha(colors.primary, 0)} />
              </linearGradient>
            </defs>
            <path d={`${sparkPath} L200,34 L0,34 Z`} fill="url(#pro-spark)" />
            <path
              d={sparkPath}
              fill="none"
              stroke={colors.primary}
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Card>

        {/* ── First-Try Success ──────────────── */}
        <Card className="p-5.5">
          <div className="flex items-center gap-2.75 mb-3.5">
            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-blue-500/12">
              <Zap size={20} className="text-blue-400" />
            </div>
            <span className="text-[13px] text-(--color-text-primary)">
              First-Try Success
            </span>
          </div>
          <div className="qv-num text-[36px] mb-1.5 text-blue-400">
            {data.firstTrySuccess}%
          </div>
          <div className="text-[12.5px] text-(--color-text-muted)">
            solved on the{" "}
            <span className="text-(--color-text-secondary) font-semibold">
              first run
            </span>
          </div>
          <div className="flex items-end gap-0.75 h-8.5 mt-3 transition-[filter] duration-300 hover:filter-[drop-shadow(0_0_4px_#3b82f6)_drop-shadow(0_0_10px_#3b82f6)]">
            {Array.from({ length: segTotal }).map((_, i) => {
              const on = i < segFilled;
              return (
                <span
                  key={i}
                  className="flex-1 rounded-[1px] transition-all duration-500"
                  style={{
                    height: run ? (on ? `${12 + i * 2.2}px` : "10px") : "4px",
                    background: on
                      ? colors.blue
                      : withAlpha(colors.textPrimary, 0.12),
                    transitionDelay: `${0.35 + i * 0.05}s`,
                  }}
                />
              );
            })}
          </div>
        </Card>

        {/* ── Boxes Created ──────────────────── */}
        <Card className="p-5.5 flex flex-col">
          <div className="flex items-center gap-2.75 mb-3.5">
            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-violet-500/12">
              <RotateCw size={20} className="text-violet-400" />
            </div>
            <span className="text-[13px] text-(--color-text-primary)">
              Boxes Requested
            </span>
          </div>
          <div className="qv-num text-[36px] mb-1.5 text-(--color-text-primary)">
            {created}
          </div>
          <div className="text-[12.5px] text-(--color-text-muted)">
            <span className="text-(--color-primary) font-semibold">
              {data.created.successful} successful
            </span>{" "}
            · {data.created.failed} failed · {data.created.inProgress} in
            progress
          </div>
          <div className="mt-auto pt-3">
            <div className="flex h-1.5 rounded-full overflow-hidden bg-black/20 gap-px transition-[filter] duration-150 hover:filter-[drop-shadow(0_0_4px_var(--color-primary))_drop-shadow(0_0_8px_var(--color-primary))]">
              <div
                className="h-full transition-all duration-700"
                style={{
                  width: run
                    ? `${(data.created.successful / totalCreated) * 100}%`
                    : 0,
                  background: colors.primary,
                }}
              />
              <div
                className="h-full transition-all duration-700"
                style={{
                  width: run
                    ? `${(data.created.failed / totalCreated) * 100}%`
                    : 0,
                  background: colors.red,
                }}
              />
              <div
                className="h-full transition-all duration-700"
                style={{
                  width: run
                    ? `${(data.created.inProgress / totalCreated) * 100}%`
                    : 0,
                  background: colors.orange,
                }}
              />
            </div>
            <div className="flex gap-3 mt-2 text-[9.5px] text-(--color-text-muted)">
              <span className="flex items-center gap-1">
                <i className="w-1.5 h-1.5 rounded-[1px] bg-(--color-primary)" />
                Successful
              </span>
              <span className="flex items-center gap-1">
                <i className="w-1.5 h-1.5 rounded-[1px] bg-red-500" />
                Failed
              </span>
              <span className="flex items-center gap-1">
                <i className="w-1.5 h-1.5 rounded-[1px] bg-orange-500" />
                In Progress
              </span>
            </div>
          </div>
        </Card>

        {/* ── Tokens Remaining ───────────────── */}
        <Card className="p-5.5 flex flex-col">
          <div className="flex items-center gap-2.75 mb-3.5">
            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-orange-500/12">
              <Coins size={20} className="text-orange-400" />
            </div>
            <span className="text-[13px] text-(--color-text-primary)">
              Tokens Remaining
            </span>
          </div>
          <div className="qv-num text-[36px] mb-1.5 text-orange-500">
            {tokens}
          </div>
          <div className="text-[12.5px] text-(--color-text-muted)">
            of {fmtK(data.dailyCap)} daily cap
          </div>
          <div className="mt-auto pt-3">
            <div className="h-1.5 rounded-full overflow-hidden bg-black/20 transition-[filter] duration-150 hover:filter-[drop-shadow(0_0_4px_#f59e0b)_drop-shadow(0_0_8px_#f59e0b)]">
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{
                  width: run ? `${capUsedPct}%` : 0,
                  background: colors.orange,
                }}
              />
            </div>
            <div className="flex justify-between mt-2 text-[10.5px] text-(--color-text-muted)">
              <span>{fmtK(data.tokensUsedToday)} used today</span>
              <span className="text-orange-500 font-semibold">
                {capLeftPct}% left
              </span>
            </div>
          </div>
        </Card>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   Section 2: Token Activity (30-day token trend)
   ───────────────────────────────────────────── */
function TokenActivity({ colors }) {
  const [ref, inView] = useInView(0.05);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reload, setReload] = useState(0);
  const retry = () => setReload((n) => n + 1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const raw = await getTokenActivity();
        if (!cancelled) setData(normalizeTokenActivity(raw));
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reload]);

  const run = inView && !!data;
  const tkTotal = useCountUp(data?.total ?? 0, run);
  const tkAvg = useCountUp(data?.avg ?? 0, run);
  const tkToday = useCountUp(data?.today ?? 0, run);

  /* ── Average-line plugin (drawn as dashed rule across chart) ── */
  const avgLinePlugin = useMemo(() => {
    if (!data) return null;
    return {
      id: "avgLine",
      afterDatasetsDraw(chart) {
        const { ctx, chartArea, scales } = chart;
        if (!scales.y) return;
        const y = scales.y.getPixelForValue(data.avg);
        ctx.save();
        ctx.setLineDash([4, 5]);
        ctx.strokeStyle = withAlpha(colors.blue, 0.45);
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(chartArea.left, y);
        ctx.lineTo(chartArea.right, y);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle = colors.blue;
        ctx.font = "600 9px monospace";
        ctx.textAlign = "right";
        ctx.fillText("AVG " + fmtK(data.avg), chartArea.right - 4, y - 5);
        ctx.restore();
      },
    };
  }, [data, colors.blue]);

  /* ── Chart.js dataset ── */
  const chartData = useMemo(() => {
    if (!data) return null;
    return {
      labels: data.labels,
      datasets: [
        {
          data: data.series,
          fill: true,
          glowColor: colors.orange,
          borderWidth: 2.5,
          tension: 0.42,
          pointRadius: data.series.map((_, i) =>
            i === data.peakIdx || i === data.series.length - 1 ? 4 : 0,
          ),
          pointBackgroundColor: data.series.map((_, i) =>
            i === data.peakIdx
              ? colors.primary
              : i === data.series.length - 1
                ? colors.violet
                : colors.orange,
          ),
          pointBorderColor: colors.bg,
          pointBorderWidth: 2,
          pointHoverRadius: 6,
          pointHoverBackgroundColor: "#fff",
          pointHoverBorderColor: colors.orange,
          pointHoverBorderWidth: 3,
          borderColor: (ctx) => {
            const { ctx: c, chartArea } = ctx.chart;
            if (!chartArea) return colors.orange;
            const g = c.createLinearGradient(
              chartArea.left,
              0,
              chartArea.right,
              0,
            );
            g.addColorStop(0, colors.orange);
            g.addColorStop(0.55, "#f5b40b");
            g.addColorStop(1, colors.primary);
            return g;
          },
          backgroundColor: (ctx) => {
            const { ctx: c, chartArea } = ctx.chart;
            if (!chartArea) return withAlpha(colors.orange, 0.1);
            const g = c.createLinearGradient(
              0,
              chartArea.top,
              0,
              chartArea.bottom,
            );
            g.addColorStop(0, withAlpha(colors.orange, 0.34));
            g.addColorStop(0.5, withAlpha(colors.orange, 0.1));
            g.addColorStop(1, withAlpha(colors.orange, 0));
            return g;
          },
        },
      ],
    };
  }, [data, colors]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    animation: { duration: 1400, easing: "easeOutQuart" },
    layout: { padding: { top: 8 } },
    plugins: {
      legend: { display: false },
      tooltip: {
        ...tipPreset(colors),
        callbacks: {
          title: (items) => data?.labels[items[0].dataIndex] ?? "",
          label: (ctx) => "  " + ctx.parsed.y.toLocaleString() + " tokens",
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          maxTicksLimit: 7,
          color: colors.textMuted,
          font: { size: 10 },
        },
      },
      y: {
        grid: { color: withAlpha(colors.textPrimary, 0.035) },
        border: { display: false },
        ticks: {
          color: colors.textMuted,
          padding: 8,
          callback: (v) => (v >= 1000 ? v / 1000 + "K" : v),
        },
      },
    },
  };

  /* ── Stat tile helper ── */
  const statTile = (icon, label, value, sub, accent) => (
    <div
      className="rounded-xl p-3 bg-(--color-bg)/60 border-l-2"
      style={{ borderColor: accent }}
    >
      <div className="flex items-center gap-1.5 text-[11px] text-(--color-text-secondary) mb-1.5">
        {icon}
        {label}
      </div>
      <div className="qv-num text-[22px]" style={{ color: accent }}>
        {value}
      </div>
      <div className="text-[10.5px] text-(--color-text-muted) mt-0.5">
        {sub}
      </div>
    </div>
  );

  const ready = !loading && !!data;

  return (
    <section ref={ref}>
      <div className="mb-4">
        <SectionLabel>Token Activity</SectionLabel>
      </div>
      <Card
        className="px-6.5 py-6 qv-ping"
        style={{ "--ping-x": "100%", "--ping-y": "0%" }}
      >
        <div className="flex justify-between items-start mb-4 flex-wrap gap-3">
          <div>
            <h3 className="text-[14.5px] font-semibold flex items-center gap-2.5 text-(--color-text-primary)">
              <Activity size={19} className="text-orange-500" />
              Token Usage
            </h3>
            <p className="text-xs text-(--color-text-muted) mt-1">
              Daily consumption across the last 30 days
            </p>
          </div>
          <span className="text-[11px] text-(--color-text-secondary) font-mono">
            {ready
              ? `${data.conversations} ${
                  data.conversations === 1 ? "conversation" : "conversations"
                } · ${fmtK(data.totalAllTime)} all-time`
              : "—"}
          </span>
        </div>

        {error ? (
          <SectionError message={error} onRetry={retry} />
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
              {statTile(
                <Gauge size={13} className="text-orange-500" />,
                "Total used",
                ready ? tkTotal : "—",
                "across 30 days",
                colors.orange,
              )}
              {statTile(
                <Activity size={13} className="text-blue-400" />,
                "Daily avg",
                ready ? tkAvg : "—",
                "tokens / day",
                colors.blue,
              )}
              {statTile(
                <CalendarDays size={13} className="text-violet-400" />,
                "Today",
                ready ? tkToday : "—",
                ready ? `${fmtK(data.today)} used today` : "—",
                colors.violet,
              )}
            </div>
            <div className="h-50">
              {ready && chartData ? (
                <Line
                  data={chartData}
                  options={options}
                  plugins={
                    avgLinePlugin
                      ? [avgLinePlugin, lineGlowPlugin]
                      : [lineGlowPlugin]
                  }
                />
              ) : (
                <div className="h-full w-full rounded-lg bg-white/5 animate-pulse" />
              )}
            </div>
          </>
        )}
      </Card>
    </section>
  );
}

/* ─────────────────────────────────────────────
   Section 3: Solo Box Progression (attempt history + completion ring)
   ───────────────────────────────────────────── */
function SoloProgression({ colors }) {
  const [ref, inView] = useInView(0.05);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reload, setReload] = useState(0);
  const retry = () => setReload((n) => n + 1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const raw = await getSoloProgression();
        if (!cancelled) setData(normalizeSoloProgression(raw));
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reload]);

  const run = inView && !!data;
  const completed = useCountUp(data?.completion.completed ?? 0, run);
  const attempted = useCountUp(data?.completion.attempted ?? 0, run);

  /* ── Chart.js dataset ── */
  const chartData = useMemo(() => {
    if (!data) return null;
    return {
      labels: data.labels,
      datasets: [
        {
          label: "Attempted",
          data: data.attemptedCum,
          fill: true,
          borderColor: colors.primary,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
          tension: 0.4,
          backgroundColor: (ctx) => {
            const { ctx: c, chartArea } = ctx.chart;
            if (!chartArea) return withAlpha(colors.primary, 0.1);
            const g = c.createLinearGradient(0, 0, 0, 195);
            g.addColorStop(0, withAlpha(colors.primary, 0.18));
            g.addColorStop(1, withAlpha(colors.primary, 0));
            return g;
          },
        },
        {
          label: "Completed",
          data: data.completedCum,
          fill: true,
          borderColor: colors.blue,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
          tension: 0.4,
          backgroundColor: (ctx) => {
            const { ctx: c, chartArea } = ctx.chart;
            if (!chartArea) return withAlpha(colors.blue, 0.1);
            const g = c.createLinearGradient(0, 0, 0, 195);
            g.addColorStop(0, withAlpha(colors.blue, 0.18));
            g.addColorStop(1, withAlpha(colors.blue, 0));
            return g;
          },
        },
      ],
    };
  }, [data, colors]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { ...tipPreset(colors), mode: "index", intersect: false },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { maxTicksLimit: 6, color: colors.textMuted },
      },
      y: {
        grid: { color: withAlpha(colors.textPrimary, 0.04) },
        ticks: { color: colors.textMuted, stepSize: 5 },
      },
    },
  };

  /* ── Completion ring SVG values ── */
  const r = 45;
  const circ = 2 * Math.PI * r;
  const dash = ((data?.completion.pct ?? 0) / 100) * circ;

  const ready = !loading && !!data;

  /* ── Error state ── */
  if (error) {
    return (
      <section ref={ref}>
        <div className="mb-4">
          <SectionLabel>Solo Box Progression</SectionLabel>
        </div>
        <Card>
          <SectionError message={error} onRetry={retry} />
        </Card>
      </section>
    );
  }

  /* ── Loading skeletons ── */
  if (!ready) {
    return (
      <section ref={ref}>
        <div className="mb-4">
          <SectionLabel>Solo Box Progression</SectionLabel>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <Card className="p-5.5 lg:col-span-2">
            <div className="animate-pulse">
              <div className="h-4 w-48 rounded bg-white/5 mb-2" />
              <div className="h-3 w-64 rounded bg-white/5 mb-4" />
              <div className="h-48.75 rounded-lg bg-white/5" />
            </div>
          </Card>
          <Card className="p-6">
            <div className="animate-pulse flex flex-col items-center">
              <div className="h-4 w-36 rounded bg-white/5 mb-6 self-start" />
              <div className="w-35 h-35 rounded-full bg-white/5 mb-4" />
              <div className="w-full flex flex-col gap-3">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="h-7 rounded bg-white/5" />
                ))}
              </div>
            </div>
          </Card>
        </div>
      </section>
    );
  }

  return (
    <section ref={ref}>
      <div className="mb-4">
        <SectionLabel>Solo Box Progression</SectionLabel>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* ── Attempt history chart ── */}
        <Card className="p-5.5 lg:col-span-2">
          <div className="flex justify-between items-start mb-2">
            <div>
              <h3 className="text-[13.5px] font-semibold text-(--color-text-primary)">
                Attempt History — 7 Days
              </h3>
              <p className="text-xs text-(--color-text-muted) mt-0.5">
                Daily Box attempts and completions
              </p>
            </div>
            <div className="flex gap-3.5 text-xs">
              <span className="flex items-center gap-1.5 text-(--color-text-secondary)">
                <span className="w-2.5 h-0.75 rounded-xs bg-(--color-primary)" />
                Attempted
              </span>
              <span className="flex items-center gap-1.5 text-(--color-text-secondary)">
                <span className="w-2.5 h-0.75 rounded-xs bg-blue-500" />
                Completed
              </span>
            </div>
          </div>
          <div className="h-48.75 mt-1.5">
            <Line
              data={chartData}
              options={chartOptions}
              plugins={[lineGlowPlugin]}
            />
          </div>
        </Card>

        {/* ── Completion ring ── */}
        <Card
          className="p-6 flex flex-col items-center qv-ping"
          style={{ "--ping-x": "100%", "--ping-y": "0%" }}
        >
          <h3 className="text-[13.5px] font-semibold mb-4.5 self-start text-(--color-text-primary)">
            Completion Overview
          </h3>
          <div className="relative w-35 h-35 mb-4 transition-[filter] duration-300 hover:filter-[drop-shadow(0_0_4px_var(--color-primary))_drop-shadow(0_0_10px_var(--color-primary))]">
            <svg width="140" height="140" viewBox="0 0 100 100">
              <circle
                cx="50"
                cy="50"
                r={r}
                fill="none"
                stroke={withAlpha(colors.textPrimary, 0.07)}
                strokeWidth="8"
              />
              <circle
                cx="50"
                cy="50"
                r={r}
                fill="none"
                stroke={colors.primary}
                strokeWidth="8"
                strokeLinecap="round"
                transform="rotate(-90 50 50)"
                strokeDasharray={`${run ? dash : 0} ${circ}`}
                style={{
                  transition: "stroke-dasharray 1.2s cubic-bezier(.22,1,.36,1)",
                }}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="qv-num text-[26px] text-(--color-primary)">
                {data.completion.pct}%
              </span>
              <span className="text-[10px] text-(--color-text-muted) font-mono uppercase tracking-[0.08em] mt-0.5">
                Complete
              </span>
            </div>
          </div>
          <div className="w-full flex flex-col gap-2">
            <div className="flex justify-between text-[12.5px] py-1.5 border-b border-(--color-border)">
              <span className="flex items-center gap-1.5 text-(--color-text-secondary)">
                <span className="w-2 h-2 rounded-full bg-(--color-primary)" />
                Completed
              </span>
              <span className="font-semibold text-(--color-primary)">
                {completed}
              </span>
            </div>
            <div className="flex justify-between text-[12.5px] py-1.5 border-b border-(--color-border)">
              <span className="flex items-center gap-1.5 text-(--color-text-secondary)">
                <span className="w-2 h-2 rounded-full bg-blue-500" />
                Untouched
              </span>
              <span className="font-semibold text-blue-400">
                {data.completion.untouched}
              </span>
            </div>
            <div className="flex justify-between text-[12.5px] py-1.5 border-b border-(--color-border)">
              <span className="flex items-center gap-1.5 text-(--color-text-secondary)">
                <span className="w-2 h-2 rounded-full bg-orange-500" />
                Attempted
              </span>
              <span className="font-semibold text-(--color-text-primary)">
                {attempted}
              </span>
            </div>
          </div>
        </Card>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   Section 4: Category Coverage & Recommendations
   ───────────────────────────────────────────── */
function CategoryCoverage({ colors }) {
  const [ref] = useInView(0.05);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reload, setReload] = useState(0);
  const retry = () => setReload((n) => n + 1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const raw = await getCategoryCoverage();
        if (!cancelled) setData(normalizeCategoryCoverage(raw));
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reload]);

  /* ── Chart.js dataset ── */
  const chartData = useMemo(() => {
    if (!data) return null;
    /* Color helper: green ≥75, blue ≥40, orange >0, red =0 */
    const colorFor = (v, hover) =>
      v >= 75
        ? withAlpha(colors.primary, hover ? 0.85 : 0.65)
        : v >= 40
          ? withAlpha(colors.blue, hover ? 0.8 : 0.6)
          : v > 0
            ? withAlpha(colors.orange, hover ? 0.8 : 0.6)
            : withAlpha(colors.red, hover ? 0.6 : 0.4);
    return {
      labels: data.chart.labels,
      datasets: [
        {
          data: data.chart.values,
          backgroundColor: data.chart.values.map((v) => colorFor(v, false)),
          hoverBackgroundColor: data.chart.values.map((v) => colorFor(v, true)),
          borderRadius: 5,
          borderSkipped: false,
        },
      ],
    };
  }, [data, colors]);

  const chartOptions = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        ...tipPreset(colors),
        callbacks: {
          label: (ctx) => ` ${ctx.parsed.x}% complete`,
        },
      },
    },
    scales: {
      x: {
        max: 100,
        grid: { color: withAlpha(colors.textPrimary, 0.04) },
        ticks: {
          color: colors.textMuted,
          callback: (v) => v + "%",
        },
      },
      y: {
        grid: { display: false },
        ticks: { color: colors.textSecondary, font: { size: 11 } },
      },
    },
  };

  const ready = !loading && !!data;

  /* ── Error state ── */
  if (error) {
    return (
      <section ref={ref}>
        <div className="mb-4">
          <SectionLabel>Category Coverage &amp; Recommendations</SectionLabel>
        </div>
        <Card>
          <SectionError message={error} onRetry={retry} />
        </Card>
      </section>
    );
  }

  /* ── Loading skeletons ── */
  if (!ready) {
    return (
      <section ref={ref}>
        <div className="mb-4">
          <SectionLabel>Category Coverage &amp; Recommendations</SectionLabel>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <Card className="p-5.5 lg:col-span-2">
            <div className="animate-pulse">
              <div className="h-4 w-52 rounded bg-white/5 mb-4" />
              <div className="h-65 rounded-lg bg-white/5" />
            </div>
          </Card>
          <div className="flex flex-col gap-4.5">
            {[0, 1].map((i) => (
              <Card key={i} className="p-5">
                <div className="animate-pulse flex flex-col gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-white/5" />
                    <div className="h-8 w-16 rounded bg-white/5" />
                  </div>
                  <div className="h-5 w-40 rounded bg-white/5" />
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section ref={ref}>
      <div className="mb-4">
        <SectionLabel>Category Coverage &amp; Recommendations</SectionLabel>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* ── Bar chart ── */}
        <Card className="p-5.5 lg:col-span-2">
          <h3 className="text-[13.5px] font-semibold mb-3.5 text-(--color-text-primary)">
            Completion % by Category
          </h3>
          <div className="h-65">
            <Bar
              data={chartData}
              options={chartOptions}
              plugins={[barGlowPlugin]}
            />
          </div>
        </Card>

        {/* ── Recommendation cards ── */}
        <div className="flex flex-col gap-4.5">
          {/* Fully Mastered */}
          <Card className="p-5">
            <div className="flex items-center gap-3 mb-3.5">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-(--color-primary)/15">
                <Trophy size={22} className="text-(--color-primary)" />
              </div>
              <div>
                <div className="text-[10.5px] font-mono uppercase tracking-widest text-(--color-text-muted) font-semibold">
                  Fully Mastered
                </div>
                <div className="qv-num text-[28px] text-(--color-primary)">
                  {data.mastered.length}
                </div>
              </div>
            </div>
            {data.mastered.length > 0 ? (
              <div className="flex flex-col gap-1.5 text-[12.5px] text-(--color-text-secondary)">
                {data.mastered.map((m) => (
                  <div key={m} className="flex items-center gap-1.5">
                    <Check size={15} className="text-(--color-primary)" />
                    {m}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-[12.5px] text-(--color-text-muted)">
                No categories fully mastered yet.
              </div>
            )}
          </Card>

          {/* Untouched */}
          <Card className="p-5">
            <div className="flex items-center gap-3 mb-3.5">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-red-500/12">
                <Compass size={22} className="text-red-500" />
              </div>
              <div>
                <div className="text-[10.5px] font-mono uppercase tracking-widest text-(--color-text-muted) font-semibold">
                  Untouched
                </div>
                <div className="qv-num text-[28px] text-red-500">
                  {data.untouched.length}
                </div>
              </div>
            </div>
            {data.untouched.length > 0 ? (
              <div className="flex flex-col gap-2">
                {data.untouched.map((u) => (
                  <div
                    key={u}
                    className="rounded-lg p-2.5 text-[12.5px] bg-red-500/12 border border-red-500/20"
                  >
                    <div className="text-red-500 font-semibold">{u}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-[12.5px] text-(--color-text-muted)">
                All categories have been started!
              </div>
            )}
          </Card>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   Section 5: Difficulty Breakdown (per-difficulty solved count + avg time)
   ───────────────────────────────────────────── */
function DifficultyStats({ colors }) {
  const [ref, inView] = useInView(0.1);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reload, setReload] = useState(0);
  const retry = () => setReload((n) => n + 1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const raw = await getDifficultyStats();
        if (!cancelled) setData(normalizeDifficultyStats(raw));
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reload]);

  const run = inView && !!data;
  const totalCompleted = useCountUp(data?.totalCompleted ?? 0, run);

  const DIFF_CFG = {
    easy: {
      label: "Easy",
      color: colors.primary,
      bg: "bg-(--color-primary)/15",
      iconBg: "bg-(--color-primary)/15",
    },
    medium: {
      label: "Medium",
      color: colors.orange,
      bg: "bg-orange-500/15",
      iconBg: "bg-orange-500/15",
    },
    hard: {
      label: "Hard",
      color: colors.red,
      bg: "bg-red-500/15",
      iconBg: "bg-red-500/15",
    },
  };

  const ready = !loading && !!data;

  /* ── Error state ── */
  if (error) {
    return (
      <section ref={ref}>
        <div className="mb-4">
          <SectionLabel>Difficulty Breakdown</SectionLabel>
        </div>
        <Card>
          <SectionError message={error} onRetry={retry} />
        </Card>
      </section>
    );
  }

  /* ── Loading skeleton ── */
  if (!ready) {
    return (
      <section ref={ref}>
        <div className="mb-4">
          <SectionLabel>Difficulty Breakdown</SectionLabel>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4.5">
          {[0, 1, 2].map((i) => (
            <Card key={i} className="p-5.5">
              <div className="animate-pulse flex flex-col gap-3">
                <div className="flex items-center gap-2.75">
                  <div className="w-10 h-10 rounded-full bg-white/5" />
                  <div className="h-3 w-20 rounded bg-white/5" />
                </div>
                <div className="h-10 w-16 rounded bg-white/5" />
                <div className="h-1.5 rounded-full bg-white/5" />
                <div className="h-14 rounded-xl bg-white/5" />
              </div>
            </Card>
          ))}
        </div>
      </section>
    );
  }

  /* ── Max completed for relative bar sizing ── */
  const maxCompleted = Math.max(...data.buckets.map((b) => b.completed), 1);

  return (
    <section ref={ref}>
      <div className="flex items-center gap-2.5 mb-4">
        <SectionLabel>Difficulty Breakdown</SectionLabel>
        <span className="inline-flex items-center px-2.25 py-0.75 rounded-md text-[11px] font-semibold bg-(--color-primary)/10 text-(--color-primary) border border-(--color-primary)/30">
          {totalCompleted} solved
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4.5">
        {data.buckets.map((b) => {
          const cfg = DIFF_CFG[b.difficulty];
          const barPct =
            maxCompleted > 0
              ? Math.max(
                  (b.completed / maxCompleted) * 100,
                  b.completed > 0 ? 4 : 0,
                )
              : 0;

          return (
            <Card
              key={b.difficulty}
              className="p-5.5 qv-ping"
              style={{ "--ping-x": "85%", "--ping-y": "20%" }}
            >
              {/* Header */}
              <div className="flex items-center gap-2.75 mb-3.5">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center ${cfg.iconBg}`}
                >
                  <BarChart3 size={18} style={{ color: cfg.color }} />
                </div>
                <span className="text-[13px] font-semibold text-(--color-text-primary)">
                  {cfg.label}
                </span>
              </div>

              {/* Completed count */}
              <div
                className="qv-num text-[36px] mb-1"
                style={{ color: cfg.color }}
              >
                {b.completed}
              </div>
              <div className="text-[12px] text-(--color-text-muted) mb-3">
                boxes completed
              </div>

              {/* Relative bar */}
              <div className="h-1.5 rounded-full overflow-hidden bg-white/5 mb-4">
                <div
                  className="h-full rounded-full transition-all duration-1000 ease-out"
                  style={{
                    width: run ? `${barPct}%` : "0%",
                    background: cfg.color,
                  }}
                />
              </div>

              {/* Avg completion time tile */}
              <div className="rounded-xl px-3.5 py-3 border border-(--color-border) bg-(--color-bg)/60">
                <div className="flex items-center gap-1.5 text-[10.5px] text-(--color-text-muted) mb-1">
                  <Timer size={13} style={{ color: cfg.color }} />
                  <span className="font-mono uppercase tracking-[0.06em] font-semibold">
                    Avg Time
                  </span>
                </div>
                <div
                  className="qv-num text-[20px] font-semibold"
                  style={{
                    color: b.avgMinutes > 0 ? cfg.color : colors.textMuted,
                  }}
                >
                  {b.avgLabel}
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   Section 6: Activity & Deployments (30-day flow-run line + box-request ring)
   ───────────────────────────────────────────── */
function ActivityDeployments({ colors }) {
  const [ref, inView] = useInView(0.05);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reload, setReload] = useState(0);
  const retry = () => setReload((n) => n + 1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const raw = await getActivityDeployments();
        if (!cancelled) setData(normalizeActivityDeployments(raw));
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reload]);

  const run = inView && !!data;
  const requested = useCountUp(data?.summary.requested ?? 0, run);
  const [ringHover, setRingHover] = useState(false);

  const flowData = useMemo(() => {
    if (!data) return null;
    return {
      labels: data.daily.labels,
      datasets: [
        {
          label: "Completed",
          data: data.daily.completed,
          fill: true,
          borderColor: colors.primary,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
          tension: 0.4,
          backgroundColor: (ctx) => {
            const { ctx: c, chartArea } = ctx.chart;
            if (!chartArea) return withAlpha(colors.primary, 0.1);
            const g = c.createLinearGradient(0, 0, 0, 160);
            g.addColorStop(0, withAlpha(colors.primary, 0.18));
            g.addColorStop(1, withAlpha(colors.primary, 0));
            return g;
          },
        },
        {
          label: "Failed",
          data: data.daily.failed,
          fill: true,
          borderColor: colors.red,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
          tension: 0.4,
          backgroundColor: (ctx) => {
            const { ctx: c, chartArea } = ctx.chart;
            if (!chartArea) return withAlpha(colors.red, 0.1);
            const g = c.createLinearGradient(0, 0, 0, 160);
            g.addColorStop(0, withAlpha(colors.red, 0.18));
            g.addColorStop(1, withAlpha(colors.red, 0));
            return g;
          },
        },
      ],
    };
  }, [data, colors]);

  const flowOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: { ...tipPreset(colors), mode: "index", intersect: false },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: colors.textMuted, maxTicksLimit: 7 },
      },
      y: {
        grid: { color: withAlpha(colors.textPrimary, 0.04) },
        ticks: { color: colors.textMuted, precision: 0 },
      },
    },
  };

  const ringData = useMemo(() => {
    if (!data) return null;
    return {
      datasets: [
        {
          data: [data.summary.successRate, 100 - data.summary.successRate],
          backgroundColor: [
            colors.primary,
            withAlpha(colors.textPrimary, 0.08),
          ],
          borderColor: "transparent",
          borderWidth: 0,
          hoverOffset: 0,
        },
      ],
    };
  }, [data, colors]);

  const ringOptions = {
    cutout: "78%",
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { enabled: false } },
    animation: { animateRotate: true, duration: 1400 },
  };

  if (error) {
    return (
      <section ref={ref}>
        <div className="mb-4">
          <SectionLabel>Activity &amp; Deployments</SectionLabel>
        </div>
        <Card>
          <SectionError message={error} onRetry={retry} />
        </Card>
      </section>
    );
  }

  const ready = !loading && !!data;

  return (
    <section ref={ref}>
      <div className="mb-4">
        <SectionLabel>Activity &amp; Deployments</SectionLabel>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
        {/* Flow-run chart */}
        <Card className="p-5.5 xl:col-span-2">
          <div className="flex justify-between items-center mb-1 gap-3 flex-wrap">
            <div>
              <h3 className="text-[13.5px] font-semibold text-(--color-text-primary)">
                Flow Runs — Daily Breakdown
              </h3>
              <p className="text-xs text-(--color-text-muted) mt-0.5">
                Completed vs failed runs over the last 30 days
              </p>
            </div>
            <div className="flex gap-3.5 text-xs">
              <span className="flex items-center gap-1.5 text-(--color-text-secondary)">
                <span className="w-2.5 h-0.75 rounded-xs bg-(--color-primary)" />
                Completed
              </span>
              <span className="flex items-center gap-1.5 text-(--color-text-secondary)">
                <span className="w-2.5 h-0.75 rounded-xs bg-red-500" />
                Failed
              </span>
            </div>
          </div>
          <div className="h-46.25 mt-3">
            {ready ? (
              <Line
                data={flowData}
                options={flowOptions}
                plugins={[lineGlowPlugin]}
              />
            ) : (
              <div className="h-full w-full rounded-lg bg-white/5 animate-pulse" />
            )}
          </div>
        </Card>

        {/* Box-request ring */}
        <Card
          className="p-5.5 qv-ping"
          style={{ "--ping-x": "100%", "--ping-y": "100%" }}
        >
          <h3 className="text-[13.5px] font-semibold mb-4 text-(--color-text-primary)">
            Box Requests
          </h3>
          <div
            className="relative w-27.5 h-27.5 mx-auto mb-4 transition-[filter] duration-300"
            onMouseEnter={() => setRingHover(true)}
            onMouseLeave={() => setRingHover(false)}
            style={{
              filter: ringHover
                ? `drop-shadow(0 0 8px ${colors.primary}) drop-shadow(0 0 18px ${withAlpha(colors.primary, 0.35)})`
                : "none",
            }}
          >
            {ready ? (
              <>
                <Doughnut data={ringData} options={ringOptions} />
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="qv-num text-[22px] text-(--color-primary)">
                    {data.summary.successRate}%
                  </span>
                  <span className="text-[9.5px] text-(--color-text-muted) font-mono uppercase tracking-[0.08em]">
                    Success Rate
                  </span>
                </div>
              </>
            ) : (
              <div className="w-full h-full rounded-full bg-white/5 animate-pulse" />
            )}
          </div>
          <div className="flex flex-col gap-2.25">
            <div className="flex justify-between items-center text-[12.5px] pb-2 border-b border-(--color-border)">
              <span className="text-(--color-text-secondary) flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-xs bg-(--color-text-secondary) shrink-0" />
                Boxes Requested
              </span>
              <span className="font-semibold text-(--color-text-primary)">
                {ready ? requested : "—"}
              </span>
            </div>
            <div className="flex justify-between items-center text-[12.5px] pb-2 border-b border-(--color-border)">
              <span className="text-(--color-text-secondary) flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-xs bg-(--color-primary) shrink-0" />
                Completed Successfully
              </span>
              <span className="font-semibold text-(--color-primary)">
                {ready ? data.summary.completed : "—"}
              </span>
            </div>
            <div className="flex justify-between items-center text-[12.5px]">
              <span className="text-(--color-text-secondary) flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-xs bg-red-500 shrink-0" />
                Failed
              </span>
              <span className="font-semibold text-red-500">
                {ready ? data.summary.failed : "—"}
              </span>
            </div>
          </div>
        </Card>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   Page
   ───────────────────────────────────────────── */
export default function ProfessionalDashboard() {
  const colors = useThemeColors();
  const { user } = useAuth();
  const displayName = user?.name || user?.username || "User";

  return (
    <Layout>
      <div className="px-8 py-8 space-y-7">
        {/* Greeting */}
        <div>
          <div className="text-[10.5px] tracking-[0.22em] uppercase text-(--color-text-muted) flex items-center gap-2 font-mono">
            <StatusDot variant="live" size={7} /> My Dashboard
          </div>
          <h1 className="qv-serif mt-2 text-[40px] leading-[1.05] font-medium tracking-tight text-(--color-text-primary)">
            Welcome back,{" "}
            <span className="text-(--color-primary)">{displayName}</span>.
          </h1>
          <p className="mt-2 text-sm text-(--color-text-secondary)">
            Your personal progress, activity, and box stats at a glance.
          </p>
        </div>

        {colors && (
          <>
            <MyOverview colors={colors} />
            <TokenActivity colors={colors} />
            <SoloProgression colors={colors} />
            <CategoryCoverage colors={colors} />
            <DifficultyStats colors={colors} />
            <ActivityDeployments colors={colors} />
          </>
        )}
      </div>
    </Layout>
  );
}
